import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ThumbsUp, User } from '@phosphor-icons/react';
import { getItemReviews, submitReview, markReviewHelpful } from '../utils/api';
import { toast } from 'sonner';

const StarRating = ({ rating, size = 16, interactive = false, onChange }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          disabled={!interactive}
          onClick={() => interactive && onChange?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <Star
            size={size}
            weight={(hoverRating || rating) >= star ? 'fill' : 'regular'}
            className={(hoverRating || rating) >= star ? 'text-primary' : 'text-gray-300'}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewCard = ({ review, onHelpful }) => {
  const [helpful, setHelpful] = useState(review.helpful_count || 0);
  const [hasVoted, setHasVoted] = useState(false);

  const handleHelpful = async () => {
    if (hasVoted) return;
    
    try {
      await markReviewHelpful(review.id);
      setHelpful(helpful + 1);
      setHasVoted(true);
      toast.success('Thanks for your feedback!');
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-border-default py-6 last:border-0"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <User size={20} className="text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-body text-sm font-medium text-secondary">{review.customer_name}</span>
              {review.is_verified && (
                <span className="font-body text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  Verified Purchase
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <StarRating rating={review.rating} size={14} />
              <span className="font-body text-xs text-text-muted">
                {new Date(review.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {review.title && (
        <h4 className="font-body font-medium text-secondary mb-2">{review.title}</h4>
      )}
      {review.comment && (
        <p className="font-body text-sm text-text-secondary mb-4">{review.comment}</p>
      )}
      
      <button
        onClick={handleHelpful}
        disabled={hasVoted}
        className={`flex items-center gap-2 font-body text-xs transition-colors ${
          hasVoted ? 'text-primary' : 'text-text-muted hover:text-primary'
        }`}
      >
        <ThumbsUp size={14} weight={hasVoted ? 'fill' : 'regular'} />
        Helpful ({helpful})
      </button>
    </motion.div>
  );
};

const ProductReviews = ({ productId }) => {
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 0, title: '', comment: '', customer_name: '' });
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ total_reviews: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await getItemReviews(productId);
      if (response.data.success) {
        setReviews(response.data.data.reviews || []);
        setSummary(response.data.data.summary || { total_reviews: 0, average_rating: 0 });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newReview.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await submitReview({
        item_id: productId,
        customer_name: newReview.customer_name,
        rating: newReview.rating,
        title: newReview.title,
        comment: newReview.comment
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Thank you for your review!');
        setNewReview({ rating: 0, title: '', comment: '', customer_name: '' });
        setShowForm(false);
      }
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-12 pt-8 border-t border-border-default">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-32 mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12 pt-8 border-t border-border-default">
      {/* Summary */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h3 className="font-display text-2xl text-secondary mb-2">Customer Reviews</h3>
          <div className="flex items-center gap-3">
            <StarRating rating={Math.round(summary.average_rating)} size={20} />
            <span className="font-body text-lg text-secondary">{summary.average_rating.toFixed(1)}</span>
            <span className="font-body text-sm text-text-muted">({summary.total_reviews} reviews)</span>
          </div>
        </div>
        
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-secondary"
          data-testid="write-review-btn"
        >
          Write a Review
        </button>
      </div>

      {/* Review Form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleSubmit}
            className="bg-background-subtle p-6 mb-8 overflow-hidden"
          >
            <h4 className="font-display text-lg text-secondary mb-4">Write Your Review</h4>
            
            <div className="mb-4">
              <label className="block font-body text-sm text-text-secondary mb-2">Your Rating *</label>
              <StarRating
                rating={newReview.rating}
                size={28}
                interactive
                onChange={(rating) => setNewReview({ ...newReview, rating })}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Your Name *</label>
                <input
                  type="text"
                  required
                  value={newReview.customer_name}
                  onChange={(e) => setNewReview({ ...newReview, customer_name: e.target.value })}
                  className="w-full px-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Review Title</label>
                <input
                  type="text"
                  value={newReview.title}
                  onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                  className="w-full px-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Sum up your review"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block font-body text-sm text-text-secondary mb-2">Your Review</label>
              <textarea
                rows={4}
                value={newReview.comment}
                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                className="w-full px-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body resize-none"
                placeholder="Share your experience with this product..."
              />
            </div>
            
            <div className="flex gap-4">
              <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
            
            <p className="font-body text-xs text-text-muted mt-4">
              Your review will be visible after approval.
            </p>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div>
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="font-body text-text-muted">No reviews yet. Be the first to review this product!</p>
        </div>
      )}
    </div>
  );
};

export { ProductReviews, StarRating };
export default ProductReviews;
